import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';
import { rgba } from 'polished';
import * as graphql from 'generated/graphql';

import { CloseIcon } from '@treams/treams-icons';
import { Skeleton, Subheading, Switch } from '@treams/treams-ui';
import { Link, useHistory } from '@treams/treams-routes';

import Logo from 'assets/logo.svg';
import Breakpoint from 'packages/kakapo-ui-kit/helpers/Breakpoint';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';

import { useHelicopter } from './HelicopterContext';

const Element = styled(Box)<{ position: 'left' | 'active' | 'right' }>`
	@media screen and (min-width: ${Breakpoint.Medium}) {
		position: absolute;
		width: 100%;
		height: 100vh;
		overflow-x: hidden;
		overflow-y: auto;
		overscroll-behavior: contain;
		transition: 0.4s cubic-bezier(0, 1, 0, 1) all;
		display: flex;
		flex-direction: column;

		${(props) =>
			props.position === 'left' &&
			`
			transform: translateX(-50%);
			opacity: 0;
			pointer-events: none;
		`}

		${(props) =>
			props.position === 'right' &&
			`
			transform: translateX(50%);
			opacity: 0;
			pointer-events: none;
			
			transition: none;
		`}
	}

	@media screen and (max-width: ${Breakpoint.Medium}) {
		display: ${(props) => (props.position === 'active' ? 'block' : 'none')};
	}
`;

const LogoContainer = styled(Box)`
	transition: 0.4s ease-out opacity;
	min-height: 49px;

	svg {
		display: block;
		width: 140px;
		height: 50px;
	}

	img {
		display: block;
		max-width: 140px;
		max-height: 40px;
		object-fit: contain;
		object-position: left center;
	}

	&:hover {
		opacity: 0.75;

		&:hover {
			opacity: 0.5;
		}
	}
`;

const HelicopterContainer = styled(Flex)<{ $on?: boolean }>`
	border: 1px solid
		${(props) => rgba(props.$on ? props.theme.colors.primary : props.theme.colors.slateBlue, 0.25)};
	border-radius: 5px;

	font-weight: bold;
	color: ${(props) => (props.$on ? props.theme.colors.primary : props.theme.colors.slateBlue)};
	cursor: pointer;

	&:hover {
		border-color: transparent;
		color: ${(props) => props.theme.colors.primary};
		background: ${(props) => rgba(props.theme.colors.primary, 0.1)};

		&:active {
			background: ${(props) => rgba(props.theme.colors.primary, 0.25)};
		}
	}
`;

const MoreInfo = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	text-decoration: underline;
	text-align: center;
`;

export default function Drawer(
	props: PropsWithChildren<{
		position: 'left' | 'active' | 'right';
		helicopter?: boolean;
		onClose?: () => void;
	}>,
) {
	const { t } = useTranslation();

	const theme = useTheme();
	const helicopter = useHelicopter();
	const usesHelicopter = useFeature(graphql.FeatureName.Helicopter);
	const { isWorkspaceSuperuser, isManager } = useViewerRoles();
	const history = useHistory();

	return (
		<Element position={props.position} py={3} px={2}>
			<Box mb={3}>
				<Flex alignItems="center" px={2}>
					<Box flexGrow={1} flexShrink={1}>
						{theme.images.logo ? (
							<Link to="/">
								<LogoContainer flexGrow={0} flexShrink={0}>
									{theme.images.logo.startsWith('/') ? (
										<Logo />
									) : (
										<img src={theme.images.logo} alt="Logo" />
									)}
								</LogoContainer>
							</Link>
						) : (
							<Skeleton.Box width={140} height={50} />
						)}
					</Box>
					{props.onClose && (
						<Box>
							<Flex alignItems="center" onClick={props.onClose}>
								<Box mr={1}>
									<Subheading>Close</Subheading>
								</Box>
								<Box size={32}>
									<CloseIcon />
								</Box>
							</Flex>
						</Box>
					)}
				</Flex>
			</Box>
			{props.helicopter !== false && (isWorkspaceSuperuser || isManager) && usesHelicopter && (
				<Box mb={3}>
					<HelicopterContainer
						py={2}
						px={3}
						alignItems="center"
						$on={helicopter.enabled}
						onClick={() => {
							if (helicopter.enabled) {
								history.push('/');
								helicopter.disable();
							} else {
								history.push('/manager');
								helicopter.enable();
							}
						}}
					>
						<Box flexGrow={1} flexShrink={1}>
							{t('sidebar.manager.helicopter')}
						</Box>
						<Switch on={helicopter.enabled} onChange={() => {}} />
					</HelicopterContainer>
					{helicopter.enabled && (
						<MoreInfo mt={2}>
							<a href={t('sidebar.manager.helicopterInfo.link')} target="_blank" rel="noreferrer">
								{t('sidebar.manager.helicopterInfo.text')}
							</a>
						</MoreInfo>
					)}
				</Box>
			)}
			{props.children}
		</Element>
	);
}
